import type { AcceptedLine, DriverShipmentLine } from "@khazzin/api-client";
import type { PodDraft } from "../pod/pod";

/**
 * The rules of an outcome at the door (EP10-S17), kept apart from the screens
 * so a test can read them: when the Failed sheet may submit, the days a customer may ask for, and a partial's lines.
 */

/** The failure reasons, in the order the sheet lists them (`03/06` §3). */
export const FAILURE_REASONS = [
	"customer_unreachable",
	"customer_rejected",
	"address_wrong",
	"customer_postponed",
	"cod_not_ready",
	"damaged_in_transit",
	"other",
] as const;

export type FailureReason = (typeof FAILURE_REASONS)[number];

/** A Failed sheet in progress, checkpointed after every step. */
export interface FailedDraft {
	kind: "failed";
	reason?: FailureReason;
	note: string;
	/** `YYYY-MM-DD`, with `customer_postponed`. */
	requestedDate: string;
	/** The photo's id in the `media` store. */
	photoId?: string;
}

/** A Partial picker in progress: units kept, by order line. */
export interface PartialDraft {
	kind: "partial";
	kept: Record<string, number>;
}

/** Every capture a stop checkpoints, the proof of delivery (EP10-S18) included. */
export type Draft = FailedDraft | PartialDraft | PodDraft;

export const emptyFailed = (): FailedDraft => ({ kind: "failed", note: "", requestedDate: "" });

/** Whether a Failed sheet holds something a close would lose (ADJ-M03-4). */
export function failedStarted(d: FailedDraft): boolean {
	return (
		d.reason !== undefined ||
		d.note.trim() !== "" ||
		d.requestedDate !== "" ||
		d.photoId !== undefined
	);
}

/**
 * Whether the Failed sheet may submit: a reason, a note with `other`, a day
 * with a postponement, and a photo whatever the reason (owner, ADJ-1022-8b;
 * the server refuses a failure without one, `422 photo_required`). Location
 * is still read at submit and sent when the phone has it.
 */
export function canSubmitFailed(d: FailedDraft): boolean {
	switch (true) {
		case d.reason === undefined:
			return false;
		case d.reason === "other" && d.note.trim() === "":
			return false;
		case d.reason === "customer_postponed" && d.requestedDate === "":
			return false;
		default:
			return d.photoId !== undefined;
	}
}

/** `YYYY-MM-DD` of the phone's local day, `days` after `now`. */
function localDay(now: Date, days: number): string {
	const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The days a customer may ask for: tomorrow through a week ahead (EP12-S10's rule). */
export function requestableDays(now: Date): { min: string; max: string } {
	return { min: localDay(now, 1), max: localDay(now, 7) };
}

/**
 * A parcel still on the van, not yet out: Delivered or Failed on it takes it
 * out first, `start_delivery` queued ahead of the outcome (ADJ-1022-8b).
 */
export function canStart(status: string): boolean {
	return status === "picked_up" || status === "at_delivery_warehouse";
}

/** Every unit kept: where a partial picker starts. */
export function allKept(lines: DriverShipmentLine[]): Record<string, number> {
	return Object.fromEntries(lines.map((l) => [l.orderLineId, l.quantity]));
}

/** The kept lines on the wire, a line with none kept left out. */
export function acceptedLines(
	lines: DriverShipmentLine[],
	kept: Record<string, number>,
): AcceptedLine[] {
	return lines.flatMap((l) => {
		const quantity = kept[l.orderLineId] ?? 0;
		return quantity > 0 ? [{ orderLineId: l.orderLineId, quantity }] : [];
	});
}

/**
 * A partial keeps something and refuses something: nothing kept is a failed
 * attempt, and everything kept is a delivery.
 */
export function isPartial(lines: DriverShipmentLine[], kept: Record<string, number>): boolean {
	let total = 0;
	let taken = 0;
	for (const l of lines) {
		total += l.quantity;
		taken += kept[l.orderLineId] ?? 0;
	}
	return taken > 0 && taken < total;
}
